/**
 * Image Compression Utility
 * Uses browser-native Canvas API for client-side image compression
 * Zero dependencies, works in all modern browsers
 */

/**
 * Compress an image file using Canvas API
 * @param {File} file - Image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width in pixels (default: 1920)
 * @param {number} options.maxHeight - Maximum height in pixels (default: 1920)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @param {number} options.maxSizeMB - Skip compression if file is smaller (default: 0.5)
 * @returns {Promise<File>} Compressed image file
 */
export async function compressImage(file, options = {}) {
    const {
        maxWidth = 1920,
        maxHeight = 1920,
        quality = 0.8,
        maxSizeMB = 0.5, // Skip compression if file < 500KB
    } = options;

    // Validate input
    if (!file || !(file instanceof File)) {
        throw new Error('Invalid file provided');
    }

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
        console.warn('File is not an image, returning original:', file.type);
        return file;
    }

    // Skip compression for small files
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB < maxSizeMB) {
        console.log(`File is ${fileSizeMB.toFixed(2)}MB, skipping compression`);
        return file;
    }

    // Skip compression for GIFs (to preserve animation)
    if (file.type === 'image/gif') {
        console.log('GIF detected, skipping compression to preserve animation');
        return file;
    }

    try {
        console.log(`Compressing image: ${file.name} (${fileSizeMB.toFixed(2)}MB)`);

        // Load image
        const image = await loadImage(file);

        // Calculate new dimensions maintaining aspect ratio
        const { width, height } = calculateDimensions(
            image.width,
            image.height,
            maxWidth,
            maxHeight
        );

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');

        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw image
        ctx.drawImage(image, 0, 0, width, height);

        // Convert canvas to blob
        const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const blob = await canvasToBlob(canvas, mimeType, quality);

        // Create new file from blob
        const compressedFile = new File([blob], file.name, {
            type: mimeType,
            lastModified: Date.now(),
        });

        // Log compression results
        const originalSizeMB = file.size / (1024 * 1024);
        const compressedSizeMB = compressedFile.size / (1024 * 1024);

        // Only use compressed version if it's actually smaller
        if (compressedFile.size >= file.size) {
            console.log(`⚠️  Compression increased size: ${originalSizeMB.toFixed(2)}MB → ${compressedSizeMB.toFixed(2)}MB (returning original)`);
            return file;
        }

        const savings = ((1 - compressedSizeMB / originalSizeMB) * 100).toFixed(1);
        console.log(`✅ Compressed: ${originalSizeMB.toFixed(2)}MB → ${compressedSizeMB.toFixed(2)}MB (${savings}% smaller)`);
        console.log(`   Dimensions: ${image.width}x${image.height} → ${width}x${height}`);

        return compressedFile;
    } catch (error) {
        console.error('Image compression failed, returning original:', error);
        return file; // Return original on error
    }
}

/**
 * Compress multiple images
 * @param {File[]} files - Array of image files
 * @param {Object} options - Compression options
 * @returns {Promise<File[]>} Array of compressed files
 */
export async function compressImages(files, options = {}) {
    if (!Array.isArray(files)) {
        throw new Error('files must be an array');
    }

    const compressionPromises = files.map(file => compressImage(file, options));
    return Promise.all(compressionPromises);
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Load image file into Image element
 * @param {File} file - Image file
 * @returns {Promise<HTMLImageElement>} Loaded image
 */
function loadImage(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const img = new Image();

            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('Failed to load image'));

            img.src = e.target.result;
        };

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

/**
 * Calculate new dimensions maintaining aspect ratio
 * @param {number} width - Original width
 * @param {number} height - Original height
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @returns {{ width: number, height: number }} New dimensions
 */
function calculateDimensions(width, height, maxWidth, maxHeight) {
    // If image is smaller than max, keep original size
    if (width <= maxWidth && height <= maxHeight) {
        return { width, height };
    }

    // Calculate aspect ratio
    const aspectRatio = width / height;

    // Determine which dimension to constrain
    if (width > height) {
        // Landscape - constrain by width
        const newWidth = Math.min(width, maxWidth);
        const newHeight = Math.round(newWidth / aspectRatio);
        return { width: newWidth, height: newHeight };
    }

    // Portrait or square - constrain by height
    const newHeight = Math.min(height, maxHeight);
    const newWidth = Math.round(newHeight * aspectRatio);
    return { width: newWidth, height: newHeight };
}

/**
 * Convert canvas to blob
 * @param {HTMLCanvasElement} canvas - Canvas element
 * @param {string} mimeType - Output MIME type
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<Blob>} Blob
 */
function canvasToBlob(canvas, mimeType, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Canvas to blob conversion failed'));
                }
            },
            mimeType,
            quality
        );
    });
}

/**
 * Check if compression is supported
 * @returns {boolean} True if Canvas API is supported
 */
export function isCompressionSupported() {
    try {
        const canvas = document.createElement('canvas');
        return !!(canvas.getContext && canvas.getContext('2d'));
    } catch (e) {
        return false;
    }
}
